import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Star, Download, Pencil, History, Loader2, Archive,
  RotateCcw, ChevronDown, ChevronUp, Eye, User, Clock,
} from 'lucide-react';
import { sopApi, downloadOriginal, formatBytes, formatDate, formatRelative } from './api';
import UniversalViewer from './viewers/UniversalViewer';
import TagChip from './components/TagChip';
import SopCard from './components/SopCard';

// While the converter is working the page re-fetches on this cadence; a typical
// office document settles in a few seconds.
const POLL_MS = 2000;

const SopViewer = () => {
  const { sopId } = useParams();
  const navigate = useNavigate();

  const [doc, setDoc] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showVersions, setShowVersions] = useState(false);
  const [activeVersionId, setActiveVersionId] = useState(null);
  const [busy, setBusy] = useState(false);

  // A view is recorded once per mount, not on every poll or re-render.
  const viewRecorded = useRef(false);

  const load = useCallback(async () => {
    try {
      const res = await sopApi.get(sopId);
      setDoc(res.data);
      setError('');
      return res.data;
    } catch (err) {
      setError(err.status === 404 ? 'That SOP no longer exists.' : (err.message || 'Failed to load SOP'));
      return null;
    } finally {
      setLoading(false);
    }
  }, [sopId]);

  useEffect(() => {
    setLoading(true);
    setActiveVersionId(null);
    viewRecorded.current = false;
    load();
  }, [load]);

  useEffect(() => {
    if (!doc || viewRecorded.current) return;
    viewRecorded.current = true;
    sopApi.recordView(doc.id).catch(() => {});
    sopApi.related(doc.id).then((r) => setRelated(r.data || [])).catch(() => setRelated([]));
  }, [doc]);

  // Poll only while a conversion is actually in flight.
  useEffect(() => {
    if (!doc) return undefined;
    const pending = doc.renderStatus === 'pending' || doc.renderStatus === 'processing';
    if (!pending) return undefined;
    const timer = setInterval(load, POLL_MS);
    return () => clearInterval(timer);
  }, [doc, load]);

  const toggleFavorite = async () => {
    const res = await sopApi.toggleFavorite(doc.id);
    setDoc((prev) => ({ ...prev, isFavorite: res.data.isFavorite }));
  };

  const handleDownload = async () => {
    setBusy(true);
    try {
      await downloadOriginal(doc.id, activeVersionId, doc.originalName);
    } catch (err) {
      setError(err.message || 'Download failed');
    } finally {
      setBusy(false);
    }
  };

  const handleRestore = async (versionId) => {
    setBusy(true);
    try {
      await sopApi.restoreVersion(doc.id, versionId);
      setActiveVersionId(null);
      await load();
    } catch (err) {
      setError(err.message || 'Restore failed');
    } finally {
      setBusy(false);
    }
  };

  const handleArchive = async () => {
    if (!window.confirm('Archive this SOP? It will be hidden from the library but kept on record.')) return;
    setBusy(true);
    try {
      await sopApi.remove(doc.id);
      navigate('/tiles/ksp_sops/library');
    } catch (err) {
      setError(err.message || 'Archive failed');
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-slate-400">
        <Loader2 className="w-6 h-6 animate-spin mr-2" />
        Loading SOP…
      </div>
    );
  }

  if (error && !doc) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <Link to="/tiles/ksp_sops/library" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-slate-200 mb-4">
          <ArrowLeft className="w-4 h-4" /> Back to library
        </Link>
        <div className="p-4 bg-rose-500/10 border border-rose-500/30 rounded-lg text-rose-300 text-sm">{error}</div>
      </div>
    );
  }

  const viewingOldVersion = activeVersionId && activeVersionId !== doc.currentVersionId;
  const activeVersion = doc.versions.find((v) => v.id === (activeVersionId || doc.currentVersionId));

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-5">
      <Link
        to="/tiles/ksp_sops/library"
        className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-slate-200"
      >
        <ArrowLeft className="w-4 h-4" /> Back to library
      </Link>

      {error && (
        <div className="p-3 bg-rose-500/10 border border-rose-500/30 rounded-lg text-rose-300 text-sm">{error}</div>
      )}

      {doc.status !== 'published' && (
        <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-amber-300 text-sm">
          This SOP is {doc.status} and is not visible in the main library.
        </div>
      )}

      {/* --- header --- */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 flex-1 space-y-2">
          <h2 className="text-2xl font-bold text-slate-100">{doc.title}</h2>
          {doc.description && <p className="text-slate-400 max-w-3xl">{doc.description}</p>}
          {doc.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 pt-1">
              {doc.tags.map((t) => (
                <Link key={t.id} to={`/tiles/ksp_sops/library?tag=${t.id}`}>
                  <TagChip tag={t} size="xs" />
                </Link>
              ))}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-4 text-xs text-slate-500 pt-1">
            {doc.updatedBy && (
              <span className="inline-flex items-center gap-1">
                <User className="w-3.5 h-3.5" /> {doc.updatedBy}
              </span>
            )}
            <span className="inline-flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" /> Updated {formatRelative(doc.updatedAt || doc.createdAt)}
            </span>
            <span className="inline-flex items-center gap-1">
              <Eye className="w-3.5 h-3.5" /> {doc.viewCount} views
            </span>
            {activeVersion && <span>v{activeVersion.versionNo}</span>}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            type="button"
            onClick={toggleFavorite}
            className={`p-2 rounded-md border transition ${doc.isFavorite
              ? 'bg-amber-500/15 border-amber-500/40 text-amber-400'
              : 'bg-slate-800 border-slate-700 text-slate-400 hover:text-slate-200'}`}
            aria-label={doc.isFavorite ? 'Remove from favorites' : 'Add to favorites'}
          >
            <Star className={`w-4 h-4 ${doc.isFavorite ? 'fill-amber-400' : ''}`} />
          </button>

          {doc.docKind !== 'link' && doc.docKind !== 'native' && (
            <button
              type="button"
              onClick={handleDownload}
              disabled={busy}
              className="inline-flex items-center gap-2 px-3 py-2 bg-slate-800 border border-slate-700
                hover:border-indigo-500/60 rounded-md text-sm text-slate-200 disabled:opacity-50"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Download
            </button>
          )}

          {doc.versions.length > 1 && (
            <button
              type="button"
              onClick={() => setShowVersions((v) => !v)}
              className="inline-flex items-center gap-2 px-3 py-2 bg-slate-800 border border-slate-700
                hover:border-indigo-500/60 rounded-md text-sm text-slate-200"
            >
              <History className="w-4 h-4" />
              v{doc.versionNo}
              {showVersions ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
          )}

          {doc.canEdit && (
            <Link
              to={`/tiles/ksp_sops/edit/${doc.id}`}
              className="inline-flex items-center gap-2 px-3 py-2 bg-indigo-600 hover:bg-indigo-700
                text-white rounded-md text-sm font-medium transition"
            >
              <Pencil className="w-4 h-4" /> Edit
            </Link>
          )}

          {doc.canManage && doc.status !== 'archived' && (
            <button
              type="button"
              onClick={handleArchive}
              disabled={busy}
              className="p-2 rounded-md bg-slate-800 border border-slate-700 text-slate-400
                hover:text-rose-300 hover:border-rose-500/40 disabled:opacity-50"
              aria-label="Archive SOP"
            >
              <Archive className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* --- version history --- */}
      {showVersions && (
        <div className="bg-slate-800 border border-slate-700 rounded-lg divide-y divide-slate-700">
          {doc.versions.map((v) => {
            const isCurrent = v.id === doc.currentVersionId;
            const isViewing = v.id === (activeVersionId || doc.currentVersionId);
            return (
              <div key={v.id} className="flex flex-wrap items-center gap-3 p-3">
                <span className={`text-sm font-medium ${isViewing ? 'text-indigo-300' : 'text-slate-200'}`}>
                  v{v.versionNo}
                </span>
                {isCurrent && (
                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
                    current
                  </span>
                )}
                <span className="text-xs text-slate-400 flex-1 min-w-0 truncate">
                  {v.changeNote || v.originalName || 'No note'}
                </span>
                <span className="text-xs text-slate-500">
                  {v.createdBy} · {formatDate(v.createdAt)}
                  {v.sizeBytes ? ` · ${formatBytes(v.sizeBytes)}` : ''}
                </span>
                <div className="flex items-center gap-1">
                  {!isViewing && (
                    <button
                      type="button"
                      onClick={() => setActiveVersionId(v.id)}
                      className="text-xs px-2 py-1 rounded text-slate-300 hover:bg-slate-700"
                    >
                      View
                    </button>
                  )}
                  {doc.canEdit && !isCurrent && (
                    <button
                      type="button"
                      onClick={() => handleRestore(v.id)}
                      disabled={busy}
                      className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded
                        text-indigo-300 hover:bg-slate-700 disabled:opacity-50"
                    >
                      <RotateCcw className="w-3 h-3" /> Make current
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {viewingOldVersion && (
        <div className="flex items-center justify-between gap-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-amber-300 text-sm">
          <span>You&rsquo;re viewing v{activeVersion?.versionNo}, not the current version.</span>
          <button
            type="button"
            onClick={() => setActiveVersionId(null)}
            className="text-xs px-2.5 py-1 rounded bg-amber-500/20 hover:bg-amber-500/30"
          >
            Back to current
          </button>
        </div>
      )}

      {/* --- the document itself ---
          When an older version is selected, its own render metadata and body
          replace the document's. docKind is re-derived from that version's
          file_class rather than inherited: an SOP that is markdown today may
          have been an uploaded deck at v1, and vice versa. */}
      <UniversalViewer
        doc={viewingOldVersion
          ? {
            ...doc,
            ...activeVersion,
            id: doc.id,
            title: doc.title,
            docKind: activeVersion?.fileClass === 'native' ? 'native' : 'file',
            bodyMarkdown: activeVersion?.bodyMarkdown || '',
          }
          : doc}
        versionId={viewingOldVersion ? activeVersionId : null}
      />

      {/* --- related --- */}
      {related.length > 0 && (
        <div className="space-y-3 pt-4">
          <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider">
            Related SOPs
          </h3>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((r) => <SopCard key={r.id} doc={r} view="list" />)}
          </div>
        </div>
      )}
    </div>
  );
};

export default SopViewer;
